"use client"

import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { attributeWithRetries } from "../lib/attributeWithRetries"
import {
  clearAttributionAttempt,
  clearReferralDraft,
  getReferralDraft,
  hasAttributionAttempted,
  markAttributionAttempted,
  markWelcomePending,
  onReferralStateChanged,
} from "../lib/referralStorage"
import {
  trackReferralAttributed,
  trackReferralAttributionCompleted,
  trackReferralAttributionStarted,
} from "../lib/referralTracking"

export function ReferralCapture() {
  const { state } = useConnectWallet()
  const queryClient = useQueryClient()
  const address = state.address

  useEffect(() => {
    if (!state.isAuthorized || !address) return

    const controller = new AbortController()
    // Tracks the in-flight (address, code) pair so cleanup can release its
    // session guard when we tear down before reaching a final outcome.
    let pending: string | undefined

    const run = async () => {
      const code = getReferralDraft()
      if (!code) return
      if (hasAttributionAttempted(address, code)) return

      markAttributionAttempted(address, code)
      pending = code
      trackReferralAttributionStarted(code)

      const outcome = await attributeWithRetries(code, controller.signal)
      if (outcome.type === "aborted") return
      pending = undefined

      if (outcome.type === "success") {
        trackReferralAttributionCompleted(code, {
          outcome: "success",
          attempts: outcome.attempts,
        })
        trackReferralAttributed(code)
        clearReferralDraft(code)
        markWelcomePending("success")
        queryClient.invalidateQueries({
          queryKey: ["referral-my-referrer", address],
        })
      } else if (outcome.type === "permanent") {
        trackReferralAttributionCompleted(code, {
          outcome: "permanent_rejection",
          reason: outcome.reason,
          attempts: outcome.attempts,
        })
        clearReferralDraft(code)
        // Silent for already_attributed — the banner already surfaces the
        // existing referrer, and any new link would otherwise re-fire the modal.
        if (outcome.reason !== "already_attributed") {
          markWelcomePending("not_claimable")
        }
      } else {
        trackReferralAttributionCompleted(code, {
          outcome: "exhausted",
          attempts: outcome.attempts,
        })
        // exhausted: release the session guard so the next cold load can retry
        clearAttributionAttempt(address, code)
      }
    }

    run()
    // Same-tab race: ReferralCaptureURL (root layout) writes the draft after
    // this effect's first commit, so we resume from the state-changed signal.
    const unsubscribe = onReferralStateChanged(run)

    return () => {
      controller.abort()
      unsubscribe()
      if (pending) clearAttributionAttempt(address, pending)
    }
  }, [state.isAuthorized, address, queryClient])

  return null
}
